import React, { useState } from "react";

import { fmtDate, fmtMoney, type CampaignInsightDay } from "../campanhasApi";

const W = 720;
const H = 200;
const PAD = { top: 12, right: 8, bottom: 26, left: 32 };

/**
 * Candidaturas por dia: uma série, barras finas com ponta arredondada e dica ao
 * passar o mouse (ou tocar). O gasto não entra no mesmo eixo: está nos cartões
 * e na tabela por dia.
 */
const DailyChart: React.FC<{ days: CampaignInsightDay[] }> = ({ days }) => {
  const [active, setActive] = useState<number | null>(null);
  if (!days.length) return null;

  const max = Math.max(1, ...days.map((d) => d.applications));
  const step = Math.ceil(max / 4);
  const top = step * 4;
  const innerW = W - PAD.left - PAD.right;
  const innerH = H - PAD.top - PAD.bottom;
  const slot = innerW / days.length;
  const barW = Math.max(3, Math.min(28, slot - 4));
  const y = (v: number) => PAD.top + innerH - (v / top) * innerH;
  const labelEvery = Math.ceil(days.length / 8);
  const a = active != null ? days[active] : null;

  return (
    <div className="cmpChart" onMouseLeave={() => setActive(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Candidaturas por dia">
        {[0, 1, 2, 3, 4].map((i) => (
          <g key={i}>
            <line className="cmpChart__grid" x1={PAD.left} x2={W - PAD.right} y1={y(step * i)} y2={y(step * i)} />
            <text className="cmpChart__axis" x={PAD.left - 6} y={y(step * i) + 4} textAnchor="end">
              {step * i}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = PAD.left + slot * i + (slot - barW) / 2;
          const h = Math.max(0, y(0) - y(d.applications));
          const r = Math.min(4, barW / 2, h);
          return (
            <g key={d.date}>
              <rect
                className="cmpChart__hit"
                x={PAD.left + slot * i}
                y={PAD.top}
                width={slot}
                height={innerH}
                fill="transparent"
                onMouseEnter={() => setActive(i)}
                onClick={() => setActive(i)}
              />
              {h > 0 ? (
                <path
                  className={`cmpChart__bar${active === i ? " cmpChart__bar--active" : ""}`}
                  pointerEvents="none"
                  d={`M${x},${y(0)} V${y(0) - h + r} Q${x},${y(0) - h} ${x + r},${y(0) - h} H${x + barW - r} Q${x + barW},${y(0) - h} ${x + barW},${y(0) - h + r} V${y(0)} Z`}
                />
              ) : null}
              {i % labelEvery === 0 ? (
                <text className="cmpChart__axis" x={PAD.left + slot * i + slot / 2} y={H - 8} textAnchor="middle">
                  {fmtDate(d.date).slice(0, 5)}
                </text>
              ) : null}
            </g>
          );
        })}
      </svg>
      {a && active != null ? (
        <div
          className="cmpChart__tip"
          style={{
            left: `${((PAD.left + slot * active + slot / 2) / W) * 100}%`,
            top: `${(y(a.applications) / H) * 100}%`,
          }}
        >
          {fmtDate(a.date)}: {a.applications} candidatura(s) · {fmtMoney(a.spendCents)} gastos
        </div>
      ) : null}
    </div>
  );
};

export default DailyChart;
